import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLearner, useLearnerStore } from '@/state/learnerStore';
import { composeDebriefHero, type DebriefHero } from '@/lib/debrief/hero';
import { composeDebriefNarrative } from '@/lib/debrief/narrative';
import { estimateSpokenDuration } from '@/lib/codebaby/duration';
import { MILESTONES } from '@/seed/milestones';
import { appHref } from '@/lib/nav';
import { PERSONAS_SEED, PERSONA_ORDER, type PersonaMeta } from '@/seed/personas';
import { SCENARIOS_BY_PERSONA } from '@/seed/scenarios';
import { findLesson } from '@/seed/modules';
import { AVATAR_IDS } from '@/constants/avatars';
import { COACH } from '@/constants/coach';
import AvatarMount, { type AvatarHandle } from '@/components/avatar/AvatarMount';
import { buildDebriefStartData } from '@/lib/codebaby/startData';
import type { LearnerState, SavedPlaybookMoment } from '@/types/learner';
import type { SessionMoment, SessionRecord } from '@/types/session';
import type { PersonaKey } from '@/constants/avatars';
import './DebriefScreen.css';

export default function DebriefScreen() {
  const { sessionId } = useParams();
  const learner = useLearner();
  const session = learner.session_history.find((s) => s.id === sessionId);
  if (!session) return <DebriefMissing sessionId={sessionId} />;
  return <DebriefInterior session={session} learner={learner} />;
}

function DebriefMissing({ sessionId }: { sessionId: string | undefined }) {
  return (
    <div className="debrief-missing">
      <p className="eyebrow">404</p>
      <h1>No session with id {sessionId ?? '—'}.</h1>
      <p>
        <Link to="/">Back to dashboard</Link>
      </p>
    </div>
  );
}

/* ------------------------------ Interior ------------------------------ */

function DebriefInterior({ session, learner }: { session: SessionRecord; learner: LearnerState }) {
  const hero = composeDebriefHero(session, learner);
  const persona = PERSONAS_SEED[session.persona_id];
  const scenario = SCENARIOS_BY_PERSONA[session.persona_id];
  const prior = priorAttempt(session, learner);

  const startData = useMemo(
    () => buildDebriefStartData(learner, session, scenario),
    [learner, session, scenario],
  );
  const narrative = useMemo(() => composeDebriefNarrative(session, scenario), [session, scenario]);
  const coachRef = useRef<AvatarHandle>(null);
  const [ready, setReady] = useState(false);
  const [playing, setPlaying] = useState(false);

  // Tell Coach's primary the learner is now on a debrief — keeps follow-up
  // questions grounded in this specific session if the learner asks one.
  useEffect(() => {
    if (!ready) return;
    coachRef.current?.notifyContext({
      type: 'navigation to',
      description: `The learner is reviewing the debrief for their ${persona.display_name} session — outcome ${session.outcome}, score ${session.score}, ${session.milestones_hit.length} milestones hit.`,
      url: `/debrief/${session.id}`,
    });
  }, [
    ready,
    persona.display_name,
    session.outcome,
    session.score,
    session.milestones_hit.length,
    session.id,
  ]);

  const playLine = (text: string) => {
    if (!ready || playing || !text) return;
    coachRef.current?.playResponse(text);
  };

  return (
    <div className="debrief">
      <DebriefTopBar session={session} persona={persona} />
      <main className="debrief__main">
        <CoachHero
          hero={hero}
          coachRef={coachRef}
          startData={startData}
          onPlayerBuilt={() => setReady(true)}
          onResponse={() => setPlaying(true)}
          onSegmentEnded={() => setPlaying(false)}
          onPlayFullDebrief={() => playLine(narrative)}
          narrativeDuration={estimateSpokenDuration(narrative)}
          canPlay={ready && !playing}
          playing={playing}
        />
        <StatsStrip session={session} totalMilestones={scenario.milestones.length} prior={prior} />
        <MomentsList
          session={session}
          learner={learner}
          onHearTake={(m) => playLine(m.coach_note)}
          canPlay={ready && !playing}
        />
        <SkillSignals session={session} totalMilestones={scenario.milestones.length} />
        <WhatsNext session={session} learner={learner} />
        <DebriefFooter session={session} />
      </main>
    </div>
  );
}

/* ------------------------------- Top bar ------------------------------ */

function DebriefTopBar({ session, persona }: { session: SessionRecord; persona: PersonaMeta }) {
  return (
    <header className="debrief-topbar">
      <Link to="/" className="debrief-topbar__back">
        <ChevronLeft /> Back to dashboard
      </Link>
      <nav className="debrief-topbar__crumb" aria-label="Breadcrumb">
        <span className="debrief-topbar__crumb-eyebrow">Debrief</span>
        <span aria-hidden> · </span>
        <span>{persona.display_name}</span>
        <span aria-hidden> · </span>
        <span>attempt {session.attempt_number}</span>
      </nav>
      <Link to="/" className="debrief-topbar__close" aria-label="Close debrief">
        <CloseIcon />
      </Link>
    </header>
  );
}

/* ------------------------------ Coach hero ---------------------------- */

function CoachHero({
  hero,
  coachRef,
  startData,
  onPlayerBuilt,
  onResponse,
  onSegmentEnded,
  onPlayFullDebrief,
  narrativeDuration,
  canPlay,
  playing,
}: {
  hero: DebriefHero;
  coachRef: React.RefObject<AvatarHandle>;
  startData: ReturnType<typeof buildDebriefStartData>;
  onPlayerBuilt: () => void;
  onResponse: () => void;
  onSegmentEnded: () => void;
  onPlayFullDebrief: () => void;
  narrativeDuration: string;
  canPlay: boolean;
  playing: boolean;
}) {
  return (
    <section className="hero">
      <div className="hero__stage">
        <AvatarMount
          ref={coachRef}
          loaderId={AVATAR_IDS.coach}
          startData={startData}
          ariaLabel={`${COACH.fullName}, coach`}
          onPlayerBuilt={onPlayerBuilt}
          onResponse={onResponse}
          onSegmentEnded={onSegmentEnded}
        />
        <span className="hero__ready-pill">
          <span className="hero__ready-dot" aria-hidden /> Ready to talk
        </span>
      </div>
      <div className="hero__content">
        <div className="hero__pills">
          <span className={`outcome-pill outcome-pill--${hero.outcome_tone}`}>
            {hero.outcome_label}
          </span>
          <span className="score-pill">
            <span className="score-pill__value">{hero.score}</span>
            <span className="score-pill__denom"> / 100</span>
          </span>
        </div>
        <p className="hero__eyebrow">{hero.eyebrow}</p>
        <h1 className="hero__headline">{hero.headline}</h1>
        <p className="hero__body">{hero.body}</p>
        <button
          type="button"
          className="hero__play"
          onClick={onPlayFullDebrief}
          disabled={!canPlay}
          aria-pressed={playing}
        >
          <PlayIcon /> {playing ? 'Playing…' : `Play full debrief · ${narrativeDuration}`}
        </button>
      </div>
    </section>
  );
}

/* ----------------------------- Stats strip ---------------------------- */

function StatsStrip({
  session,
  totalMilestones,
  prior,
}: {
  session: SessionRecord;
  totalMilestones: number;
  prior: SessionRecord | undefined;
}) {
  const delta = prior ? session.score - prior.score : null;
  return (
    <section className="stats">
      <StatCell label="Duration" value={formatDuration(session.duration_s)} />
      <StatCell label="Exchanges" value={String(session.transcript.length)} />
      <StatCell
        label="Milestones hit"
        value={`${session.milestones_hit.length} of ${totalMilestones}`}
      />
      <StatCell
        label="Score vs last try"
        value={delta === null ? '—' : delta > 0 ? `+${delta}` : String(delta)}
        {...(delta === null && { sub: 'First try' })}
      />
    </section>
  );
}

function StatCell({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="stat-cell">
      <p className="stat-cell__label">{label}</p>
      <p className="stat-cell__value">{value}</p>
      {sub && <p className="stat-cell__sub">{sub}</p>}
    </div>
  );
}

/* ----------------------------- Moments list --------------------------- */

function MomentsList({
  session,
  learner,
  onHearTake,
  canPlay,
}: {
  session: SessionRecord;
  learner: LearnerState;
  onHearTake: (m: SessionMoment) => void;
  canPlay: boolean;
}) {
  return (
    <section>
      <p className="eyebrow">Moments that mattered</p>
      <h2 className="section-head">Three things to remember</h2>
      <ul className="moments">
        {session.moments.map((m) => (
          <MomentCard
            key={`${session.id}-${m.at_s}`}
            moment={m}
            sessionId={session.id}
            saved={isSaved(learner, session.id, m.at_s)}
            onHearTake={() => onHearTake(m)}
            canPlay={canPlay}
          />
        ))}
      </ul>
    </section>
  );
}

function isSaved(learner: LearnerState, sessionId: string, at_s: number): boolean {
  return learner.saved_playbook.some((p) => p.session_id === sessionId && p.timestamp === at_s);
}

function MomentCard({
  moment,
  sessionId,
  saved,
  onHearTake,
  canPlay,
}: {
  moment: SessionMoment;
  sessionId: string;
  saved: boolean;
  onHearTake: () => void;
  canPlay: boolean;
}) {
  const { savePlaybookMoment, removePlaybookMoment } = useLearnerStore();
  const onToggleSave = () => {
    if (saved) {
      removePlaybookMoment(sessionId, moment.at_s);
    } else {
      const payload: SavedPlaybookMoment = {
        session_id: sessionId,
        timestamp: moment.at_s,
        quote: moment.quote,
        coach_note: moment.coach_note,
        tags: deriveTagsFromNote(moment),
      };
      savePlaybookMoment(payload);
    }
  };
  return (
    <li className={`moment moment--${moment.type}`}>
      <div className="moment__head">
        <span className={`moment__icon moment__icon--${moment.type}`} aria-hidden>
          {moment.type === 'win' ? <CheckIcon /> : <ArrowDownIcon />}
        </span>
        <div className="moment__head-text">
          <p className="moment__title">{moment.title}</p>
          <p className="moment__ts">{formatDuration(moment.at_s)} mark</p>
        </div>
      </div>
      <blockquote className="moment__quote">{moment.quote}</blockquote>
      <p className="moment__note">{moment.coach_note}</p>
      <div className="moment__footer">
        <button type="button" className="moment__play" onClick={onHearTake} disabled={!canPlay}>
          <PlayIcon /> Hear Coach's take · {estimateSpokenDuration(moment.coach_note)}
        </button>
        <button
          type="button"
          className={`moment__save ${saved ? 'moment__save--saved' : ''}`}
          onClick={onToggleSave}
          aria-pressed={saved}
        >
          {saved ? (
            <>
              <CheckIcon /> Saved to playbook
            </>
          ) : (
            <>
              <BookmarkIcon /> Save to playbook
            </>
          )}
        </button>
      </div>
    </li>
  );
}

function deriveTagsFromNote(m: SessionMoment): string[] {
  const tags: string[] = [];
  const t = `${m.title} ${m.coach_note}`.toLowerCase();
  if (/discovery|reframe|ask/.test(t)) tags.push('discovery');
  if (/pain|actual words|firefighting/.test(t)) tags.push('pain_quantification');
  if (/close|commit|small yes/.test(t)) tags.push('small_yes_closes');
  return tags.length ? tags : ['discovery'];
}

/* ----------------------------- Skill signals -------------------------- */

function SkillSignals({
  session,
  totalMilestones: _totalMilestones,
}: {
  session: SessionRecord;
  totalMilestones: number;
}) {
  const scenario = SCENARIOS_BY_PERSONA[session.persona_id];
  // Strengths = win-type milestones actually credited; focus = expected
  // milestones not hit. Labels come from the vertical pack (src/seed/milestones).
  const strengths = session.milestones_hit
    .filter((m) => MILESTONES[m]?.moment === 'win')
    .slice(0, 3)
    .map((m) => MILESTONES[m].label);
  const missed = scenario.milestones.filter((m) => !session.milestones_hit.includes(m));
  const focusNext = missed.slice(0, 2).map((m) => MILESTONES[m].label);

  // Reframe the right-column header by posture + outcome. When the rep
  // closed a warm customer (Kayla), the missed milestones aren't failures
  // — they're reps to bank for harder rooms. The default "Focus next time"
  // reads as a reprimand on a successful close, which is what Michelle
  // flagged 2026-05-04.
  const closed = session.outcome === 'closed';
  const warm = scenario.start_temperature >= 2.0;
  const focusHeader =
    closed && warm
      ? 'Practice for harder rooms'
      : closed
        ? 'Reps to bank for next time'
        : 'Focus next time';

  return (
    <section className="skills">
      <div className="skills__col skills__col--win">
        <p className="eyebrow skills__col-eyebrow">Strengths this session</p>
        <ul>
          {strengths.map((s) => (
            <li key={s}>
              <span className="skills__dot skills__dot--win" aria-hidden />
              {s}
            </li>
          ))}
          {strengths.length === 0 && (
            <li className="skills__empty">Nothing confident to call out yet.</li>
          )}
        </ul>
      </div>
      <div className="skills__col skills__col--growth">
        <p className="eyebrow skills__col-eyebrow">{focusHeader}</p>
        <ul>
          {focusNext.map((s) => (
            <li key={s}>
              <span className="skills__dot skills__dot--growth" aria-hidden />
              {s}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* ------------------------------ What's next --------------------------- */

function WhatsNext({ session, learner }: { session: SessionRecord; learner: LearnerState }) {
  const closed = session.outcome === 'closed';
  const persona = PERSONAS_SEED[session.persona_id];
  const nextLesson = findLesson(learner.current_module, learner.current_lesson);
  const nextPersona = nextLockedPersonaAfter(session.persona_id, learner);

  const tryAgain = (
    // Plain anchor — Coach↔persona transitions force a full page load
    // because CodeBaby's loader can't be re-initialized on the same page.
    <a
      href={appHref(`/roleplay/${SCENARIOS_BY_PERSONA[session.persona_id].scenario_id}`)}
      className="next-card next-card--primary"
    >
      <p className="next-card__eyebrow">Primary next step</p>
      <h3 className="next-card__title">Try {persona.display_name.split(' ')[0]} again</h3>
      <p className="next-card__sub">{persona.retry_hook}</p>
      <span className="next-card__cta">
        Start attempt {session.attempt_number + 1} <ArrowRight />
      </span>
    </a>
  );

  const continueLesson = (
    <Link
      to={`/lesson/${nextLesson?.lesson_id ?? 'm1-l4-skepticism-to-scope'}`}
      className="next-card next-card--secondary"
    >
      <p className="next-card__eyebrow">Keep moving</p>
      <h3 className="next-card__title">Continue to Lesson {learner.current_lesson}</h3>
      <p className="next-card__sub">
        {nextLesson?.title ?? 'Your next lesson'} — picks up right where you just left off.
      </p>
      <span className="next-card__cta next-card__cta--muted">
        Resume lesson <ArrowRight />
      </span>
    </Link>
  );

  const locked = (
    <div className="next-card next-card--locked" aria-disabled>
      <p className="next-card__eyebrow next-card__eyebrow--lock">
        <LockIcon /> Locked
      </p>
      {nextPersona ? (
        <>
          <h3 className="next-card__title">{nextPersona.display_name}</h3>
          <p className="next-card__sub">
            {nextPersona.short_label}. {nextPersona.unlock_hint}
          </p>
        </>
      ) : (
        <>
          <h3 className="next-card__title">You've unlocked them all</h3>
          <p className="next-card__sub">More personas ship with the v2 release.</p>
        </>
      )}
    </div>
  );

  return (
    <section>
      <p className="eyebrow">What's next</p>
      <h2 className="section-head">Pick your next move</h2>
      <div className="next-grid">
        {closed ? continueLesson : tryAgain}
        {closed ? tryAgain : continueLesson}
        {locked}
      </div>
    </section>
  );
}

function nextLockedPersonaAfter(
  current: PersonaKey,
  learner: LearnerState,
): PersonaMeta | undefined {
  const idx = PERSONA_ORDER.indexOf(current);
  for (let i = idx + 1; i < PERSONA_ORDER.length; i++) {
    const key = PERSONA_ORDER[i]!;
    if (!learner.personas_unlocked.includes(key)) {
      return PERSONAS_SEED[key];
    }
  }
  return undefined;
}

/* ------------------------------- Footer ------------------------------- */

function DebriefFooter({ session }: { session: SessionRecord }) {
  const [open, setOpen] = useState(false);
  return (
    <footer className="debrief-footer">
      <p className="debrief-footer__status">
        Debrief complete · {session.transcript.length} exchanges captured
      </p>
      <button type="button" className="debrief-footer__view" onClick={() => setOpen((o) => !o)}>
        {open ? 'Hide full transcript' : 'View full transcript'}
        <ArrowRight />
      </button>
      {open && <TranscriptExpanded session={session} />}
    </footer>
  );
}

function TranscriptExpanded({ session }: { session: SessionRecord }) {
  const persona = PERSONAS_SEED[session.persona_id];
  const first = persona.display_name.split(' ')[0];
  return (
    <div className="transcript-expanded">
      <ol>
        {session.transcript.map((turn, i) => (
          <li
            key={i}
            className={`transcript-expanded__turn transcript-expanded__turn--${turn.speaker}`}
          >
            <span className="transcript-expanded__who">
              {turn.speaker === 'learner' ? 'You' : first}
            </span>
            <span className="transcript-expanded__ts">{formatDuration(turn.at_s)}</span>
            <p className="transcript-expanded__text">{turn.text}</p>
          </li>
        ))}
      </ol>
    </div>
  );
}

/* ----------------------------- Utilities ------------------------------ */

function priorAttempt(s: SessionRecord, learner: LearnerState): SessionRecord | undefined {
  return learner.session_history.find(
    (x) => x.persona_id === s.persona_id && x.attempt_number === s.attempt_number - 1,
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/* -------------------------------- Icons ------------------------------- */

function ChevronLeft() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M15 6l-6 6 6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ArrowRight() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function ArrowDownIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 5v14M6 13l6 6 6-6"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M5 12.5l4.5 4.5L20 6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function PlayIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden>
      <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
    </svg>
  );
}
function CloseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 6l12 12M18 6L6 18"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}
function BookmarkIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 4h12v17l-6-3.5L6 21V4z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function LockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 11V8a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.75" />
    </svg>
  );
}
